import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { io } from 'socket.io-client';
import { ISignin } from '../types/auth.types';
import { Input } from '../../common/components/form/input.component';
import { SixDigitInput } from '../../common/components/form/six-digit-input.component';
import {
  CheckBox,
  FormCaption,
  FormSubmit,
  InformSpan
} from '../../common/components';
import { EmailErrors, AUTH_PATTERN, PasswordErrors } from './validation.pattern';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { APP_KEYS } from '../../common/consts';
import { authService } from '../service/auth.service';
import useAuth from '../feature/auth';
import { useThrottle } from '../../hooks/useThrottle';
import { setStoredToken } from '../../common/services';

type Inputs = {
  email: string;
  password: string;
  remember: any;
};

type MfaInputs = {
  code: string;
};

export const Signin = () => {
  const { t } = useTranslation();
  const { setIsAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [isLockedOut, setIsLockedOut] = React.useState(false);

  // MFA state
  const [mfaStep, setMfaStep] = React.useState(false);
  const [mfaToken, setMfaToken] = React.useState('');
  const [mfaMethods, setMfaMethods] = React.useState<{ totp: boolean; email: boolean; push: boolean }>({ totp: false, email: false, push: false });
  const [activeMethod, setActiveMethod] = React.useState<'totp' | 'email' | 'backup' | 'push'>('totp');
  const [rememberMe, setRememberMe] = React.useState(false);
  const [useBackupCode, setUseBackupCode] = React.useState(false);
  const [backupCode, setBackupCode] = React.useState('');
  const [resendCooldown, setResendCooldown] = React.useState(0);

  // Push MFA state
  const [pushChallengeId, setPushChallengeId] = React.useState('');
  const [pushStatus, setPushStatus] = React.useState<'waiting' | 'approved' | 'denied' | 'expired' | 'no_devices'>('waiting');
  const [pushTimeout, setPushTimeout] = React.useState(120);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isValid }
  } = useForm<Inputs>({ mode: 'onChange' });

  const {
    register: registerMfa,
    setError: setMfaError,
    formState: { errors: mfaErrors },
    setValue: setMfaValue
  } = useForm<MfaInputs>({ mode: 'onChange' });

  const [formattedCode, setFormattedCode] = React.useState('');

  // Resend cooldown timer
  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const sendEmailOtp = useMutation({
    mutationFn: (token: string) => authService.mfaEmailSend(token),
    onSuccess: () => setResendCooldown(60)
  });

  const sendPushChallenge = useMutation({
    mutationFn: async (token: string) => {
      // Get this browser's push endpoint so the backend can exclude it
      let excludeEndpoint: string | undefined;
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        const sub = await reg?.pushManager?.getSubscription();
        if (sub) excludeEndpoint = sub.endpoint;
      } catch {}
      return authService.mfaPushSend(token, excludeEndpoint);
    },
    onSuccess: (data) => {
      setPushChallengeId(data.challengeId);
      setPushStatus('waiting');
      setPushTimeout(120);
    },
    onError: (err: any) => {
      const status = err?.response?.status || err?.response?.data?.statusCode;
      if (status === 400) {
        setPushStatus('no_devices');
      } else if (status === 401) {
        // mfaToken expired — need to re-login
        setMfaStep(false);
        setMfaToken('');
      }
    }
  });

  // Push MFA countdown timer
  React.useEffect(() => {
    if (activeMethod !== 'push' || pushStatus !== 'waiting' || pushTimeout <= 0) return;
    const timer = setTimeout(() => {
      if (pushTimeout <= 1) {
        setPushStatus('expired');
      }
      setPushTimeout((t) => t - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [activeMethod, pushStatus, pushTimeout]);

  // Push MFA WebSocket listener — real-time approval/denial
  React.useEffect(() => {
    if (!pushChallengeId || activeMethod !== 'push' || pushStatus !== 'waiting') return;

    const backendUrl = (
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_BACKEND_URL_DEV ||
      'http://localhost:4200'
    ).replace(/\/api\/v1\/?$/, '');

    const socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000
    });

    socket.on('connect', () => {
      socket.emit('join-mfa-challenge', pushChallengeId);
    });

    socket.on('mfa-push:approved', (data: { access_token: string; user: { email: string } }) => {
      if (data.access_token) {
        setStoredToken(data.access_token);
      }
      setPushStatus('approved');
      setIsAuthenticated(true);
      checkAuth().then(() => navigate(ROUTER_LINKS.ROOT));
    });

    socket.on('mfa-push:denied', () => {
      setPushStatus('denied');
    });

    socket.on('reconnect', () => {
      socket.emit('join-mfa-challenge', pushChallengeId);
    });

    return () => {
      socket.emit('leave-mfa-challenge', pushChallengeId);
      socket.disconnect();
    };
  }, [pushChallengeId, activeMethod, pushStatus]);

  const signin = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.USER],
    mutationFn: async (data: ISignin) => authService.login(data),
    onSuccess: async (result: any) => {
      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        const methods = result.mfaMethods || { totp: true, email: false, push: false };
        setMfaMethods(methods);

        // Default to push > totp > email
        if (methods.push) {
          setActiveMethod('push');
          sendPushChallenge.mutate(result.mfaToken);
        } else if (methods.totp) {
          setActiveMethod('totp');
        } else if (methods.email) {
          setActiveMethod('email');
          sendEmailOtp.mutate(result.mfaToken);
        }

        setMfaStep(true);
        return;
      }
      setIsAuthenticated(true);
      await checkAuth();
      navigate(ROUTER_LINKS.ROOT);
    },
    onError: (err: any) => {
      const message = err.response?.data?.message ?? '';
      if (
        message.includes('Too many failed attempts') ||
        err.response?.status === 429
      ) {
        setIsLockedOut(true);
        setError('root', {
          type: 'manual',
          message: t('auth.tooManyAttempts')
        });
      } else if (message.includes('User not found')) {
        setError('email', {
          type: 'manual',
          message: t('auth.accountNotFound')
        });
      } else if (message.includes('Credentials incorrect')) {
        setError('password', {
          type: 'manual',
          message: t('auth.passwordIncorrect')
        });
      } else {
        setError('root', {
          type: 'manual',
          message: message || t('auth.genericError')
        });
      }
    }
  });
  const throttledSignin = useThrottle(signin.mutate);

  const mfaVerify = useMutation({
    mutationFn: async (code: string) =>
      authService.mfaVerify(mfaToken, code, rememberMe),
    onSuccess: async () => {
      setIsAuthenticated(true);
      await checkAuth();
      navigate(ROUTER_LINKS.ROOT);
    },
    onError: (err: any) => {
      const message = err.response?.data?.message ?? '';
      if (message.includes('expired') || message.includes('Invalid MFA token')) {
        setMfaError('code', {
          type: 'manual',
          message: t('auth.mfaTokenExpired')
        });
      } else {
        setMfaError('code', {
          type: 'manual',
          message: t('auth.mfaCodeIncorrect')
        });
      }
    }
  });

  const onSubmit: SubmitHandler<Inputs> = (data: any) => {
    setRememberMe(data.remember === true);
    throttledSignin({ email: data.email, password: data.password, remember: data.remember === true });
  };

  const handleMfaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (useBackupCode) {
      const trimmed = backupCode.trim();
      if (!trimmed) return;
      mfaVerify.mutate(trimmed);
    } else {
      const code = formattedCode.replace(/\s/g, '');
      if (code.length !== 6) return;
      mfaVerify.mutate(code);
    }
  };

  const handleBackToLogin = () => {
    setMfaStep(false);
    setMfaToken('');
    setFormattedCode('');
    setBackupCode('');
    setUseBackupCode(false);
    setActiveMethod('totp');
    setResendCooldown(0);
    setPushChallengeId('');
    setPushStatus('waiting');
    setPushTimeout(120);
  };

  const switchToEmail = () => {
    setActiveMethod('email');
    setUseBackupCode(false);
    setFormattedCode('');
    setBackupCode('');
    sendEmailOtp.mutate(mfaToken);
  };

  const switchToTotp = () => {
    setActiveMethod('totp');
    setUseBackupCode(false);
    setFormattedCode('');
    setBackupCode('');
  };

  const switchToPush = () => {
    setActiveMethod('push');
    setUseBackupCode(false);
    setFormattedCode('');
    setBackupCode('');
    setPushStatus('waiting');
    setPushTimeout(120);
    sendPushChallenge.mutate(mfaToken);
  };

  // MFA step view
  if (mfaStep) {
    const showingBackup = useBackupCode;
    const showingPush = activeMethod === 'push' && !showingBackup;
    const showingEmail = activeMethod === 'email' && !showingBackup;
    const showingTotp = activeMethod === 'totp' && !showingBackup;

    // Push MFA waiting screen
    if (showingPush) {
      return (
        <div>
          <FormCaption>{t('auth.mfaTitle')}</FormCaption>
          <InformSpan className="hidden md:block">
            {t('auth.mfaPushSubtitle')}
          </InformSpan>

          <div className="flex flex-col items-center py-[24px]">
            {pushStatus === 'waiting' && (
              <>
                <div className="w-[48px] h-[48px] mb-[16px] rounded-full bg-[#F0F9FF] dark:bg-[#0ea5c9]/10 flex items-center justify-center animate-pulse">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0ea5c9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <p className="font-body text-[16px] font-medium text-base-black dark:text-white mb-[4px]">
                  {t('auth.mfaPushWaiting')}
                </p>
                <p className="font-body text-[13px] text-[#818185] mb-[4px]">
                  {t('auth.mfaPushCheckDevice')}
                </p>
                <p className="font-body text-[12px] text-[#818185]">
                  {Math.floor(pushTimeout / 60)}:{String(pushTimeout % 60).padStart(2, '0')}
                </p>
              </>
            )}

            {pushStatus === 'denied' && (
              <>
                <div className="w-[48px] h-[48px] mb-[16px] rounded-full bg-[#FEF2F2] flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D8210C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </div>
                <p className="font-body text-[16px] font-medium text-base-black dark:text-white mb-[4px]">
                  {t('auth.mfaPushDenied')}
                </p>
              </>
            )}

            {pushStatus === 'no_devices' && (
              <>
                <div className="w-[48px] h-[48px] mb-[16px] rounded-full bg-[#FEF3C7] flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
                </div>
                <p className="font-body text-[16px] font-medium text-base-black dark:text-white mb-[4px]">
                  {t('auth.mfaPushNoDevices')}
                </p>
                <p className="font-body text-[13px] text-[#818185] text-center max-w-[300px]">
                  {t('auth.mfaPushNoDevicesDetail')}
                </p>
              </>
            )}

            {pushStatus === 'expired' && (
              <>
                <div className="w-[48px] h-[48px] mb-[16px] rounded-full bg-[#FEF3C7] flex items-center justify-center">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                </div>
                <p className="font-body text-[16px] font-medium text-base-black dark:text-white mb-[4px]">
                  {t('auth.mfaPushExpired')}
                </p>
                <button
                  type="button"
                  onClick={switchToPush}
                  disabled={sendPushChallenge.isPending}
                  className="mt-[12px] h-[36px] px-[20px] rounded-[32px] bg-base-black text-white dark:bg-[#C8C8CB] dark:text-[#1C1C1F] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
                >
                  {sendPushChallenge.isPending ? t('common.loading') : t('auth.mfaPushResend')}
                </button>
              </>
            )}
          </div>

          {/* Method switchers */}
          <div className="flex flex-col items-center gap-[10px]">
            {(pushStatus === 'denied' || pushStatus === 'expired') && mfaMethods.push && pushStatus !== 'waiting' && (
              <button
                type="button"
                onClick={switchToPush}
                disabled={sendPushChallenge.isPending}
                className="text-base-black font-body text-[14px] font-medium hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaUsePush')}
              </button>
            )}
            {mfaMethods.totp && (
              <button
                type="button"
                onClick={switchToTotp}
                className="text-base-black font-body text-[14px] font-medium hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaUseAuthenticator')}
              </button>
            )}
            {mfaMethods.email && (
              <button
                type="button"
                onClick={switchToEmail}
                className="text-base-black font-body text-[14px] font-medium hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaSendEmailInstead')}
              </button>
            )}
            {mfaMethods.totp && (
              <button
                type="button"
                onClick={() => { setActiveMethod('totp'); setUseBackupCode(true); setFormattedCode(''); setBackupCode(''); }}
                className="text-gray-900 font-body text-[14px] hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaUseBackupCode')}
              </button>
            )}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-gray-900 font-body text-[14px] hover:opacity-60 active:opacity-50 transition-opacity"
            >
              {t('auth.backToLogin')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        <FormCaption>{t('auth.mfaTitle')}</FormCaption>
        <InformSpan className="hidden md:block">
          {showingBackup
            ? t('auth.mfaBackupSubtitle')
            : showingEmail
            ? t('auth.mfaEmailSubtitle')
            : t('auth.mfaSubtitle')
          }
        </InformSpan>

        <form onSubmit={handleMfaSubmit} className="flex flex-col">
          {showingBackup ? (
            <div className="mb-[16px]">
              <label className="block font-body text-[14px] text-gray-900 mb-[8px]">
                {t('auth.mfaBackupCodeLabel')}
              </label>
              <input
                type="text"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value)}
                placeholder={t('auth.mfaBackupCodePlaceholder')}
                maxLength={8}
                className="w-full h-[44px] rounded-[24px] border border-gray-700 px-[16px] font-mono text-[14px] focus:outline-none focus:border-blue-100 focus:ring-[4px] focus:ring-[rgba(93,225,255,0.20)] transition-all"
                autoFocus
              />
              {mfaErrors.code && (
                <p className="text-red-500 font-body text-[14px] mt-[8px]">
                  {mfaErrors.code.message}
                </p>
              )}
            </div>
          ) : (
            <div className="mb-[16px]">
              <SixDigitInput
                errors={mfaErrors}
                name="code"
                register={registerMfa}
                setFormattedValue={(val) => {
                  setFormattedCode(val);
                  setMfaValue('code', val);
                }}
                formattedValue={formattedCode}
              />
            </div>
          )}

          <FormSubmit
            isDisabled={
              showingBackup
                ? !backupCode.trim()
                : formattedCode.replace(/\s/g, '').length !== 6
            }
            isLoading={mfaVerify.isPending}
            value={t('auth.mfaVerifyButton')}
          />

          <div className="flex flex-col items-center gap-[10px] mt-[4px]">
            {/* Resend for email method */}
            {showingEmail && (
              <button
                type="button"
                disabled={resendCooldown > 0 || sendEmailOtp.isPending}
                onClick={() => sendEmailOtp.mutate(mfaToken)}
                className="text-gray-900 font-body text-[14px] hover:opacity-60 active:opacity-50 transition-opacity disabled:opacity-40"
              >
                {resendCooldown > 0
                  ? `${t('auth.mfaResend')} (${resendCooldown}s)`
                  : sendEmailOtp.isPending
                  ? t('common.loading')
                  : t('auth.mfaResend')
                }
              </button>
            )}

            {/* Method switchers */}
            {showingTotp && mfaMethods.email && (
              <button
                type="button"
                onClick={switchToEmail}
                className="text-base-black font-body text-[14px] font-medium hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaSendEmailInstead')}
              </button>
            )}
            {showingEmail && mfaMethods.totp && (
              <button
                type="button"
                onClick={switchToTotp}
                className="text-base-black font-body text-[14px] font-medium hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaUseAuthenticator')}
              </button>
            )}
            {(showingTotp || showingEmail) && mfaMethods.push && (
              <button
                type="button"
                onClick={switchToPush}
                className="text-base-black font-body text-[14px] font-medium hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaUsePush')}
              </button>
            )}

            {/* Backup code toggle (only when TOTP is available) */}
            {!showingBackup && mfaMethods.totp && (
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(true);
                  setFormattedCode('');
                  setBackupCode('');
                }}
                className="text-gray-900 font-body text-[14px] hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {t('auth.mfaUseBackupCode')}
              </button>
            )}
            {showingBackup && (
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(false);
                  setBackupCode('');
                  setFormattedCode('');
                }}
                className="text-base-black font-body text-[14px] font-medium hover:opacity-60 active:opacity-50 transition-opacity"
              >
                {activeMethod === 'email' ? t('auth.mfaUseEmailCode') : t('auth.mfaUseAuthenticator')}
              </button>
            )}

            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-gray-900 font-body text-[14px] hover:opacity-60 active:opacity-50 transition-opacity"
            >
              {t('auth.backToLogin')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Normal login view
  return (
    <div>
      <FormCaption>{t('auth.loginTitle')}</FormCaption>
      <InformSpan className="hidden md:block">
        {t('auth.loginSubtitle')}
      </InformSpan>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <Input
          errors={errors}
          name="email"
          label={t('auth.email')}
          placeholder={t('auth.enterHere')}
          type="email"
          register={register.bind(null, 'email', {
            required: { message: EmailErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.email,
              message: EmailErrors.pattern
            }
          })}
        />

        <Input
          errors={errors}
          name="password"
          label={t('auth.password')}
          placeholder={t('auth.enterHere')}
          type="password"
          register={register.bind(null, 'password', {
            required: { message: PasswordErrors.required, value: true }
          })}
        />
        {errors.root && (
          <p className="text-red-500 font-body text-[14px] mb-4">
            {errors.root.message}
          </p>
        )}
        <div className="flex justify-between items-center max-w-[301px] md:max-w-[392px] mb-[22px] md:mb-[24px]">
          <CheckBox
            description={t('auth.rememberMe')}
            register={register.bind(null, 'remember')}
          />
          <Link
            to={ROUTER_LINKS.FORGOT_PASSWORD}
            className="text-base-black font-body text-[16px] font-medium hover:opacity-60 active:opacity-50"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <FormSubmit
          isDisabled={!isValid || isLockedOut}
          isLoading={signin.isPending}
          value={t('auth.loginButton')}
        />

        <div className="text-center font-body text-[16px]">
          <span className="text-gray-900">{t('auth.newToStandby')} </span>
          <Link
            to={ROUTER_LINKS.SIGNUP}
            className="text-base-black font-medium hover:opacity-60"
          >
            {t('auth.createAccount')}
          </Link>
        </div>
      </form>
    </div>
  );
};
